import { useState, useRef, useCallback } from 'react';
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import '@/components/forms/reactCropFloor.css';
import { Icons, Actions, transitions, overlays, vars } from '@/theme';
import { dropzoneClass } from './avatareditordropzone.css';
import { AspectBox } from '@/components/ui/AspectBox';
import { ActionButton } from '@/components/common/ActionButton';
import { testIds } from '@/test/testIds';
import type { CropData } from '../AvatarCropper';
import { formMessages } from '@/constants';
import { Avatar, Spinner, Text } from '@/components/primitives';
import {
  useFileRef,
  useUploadValidation,
  fileRefProcessingCopy,
  extractFileDecline,
  sameFileRef,
} from '@/features/files';
import type { FileRef } from '@/types/file';

export type { CropData };

export interface AvatarEditorProps {
  /**
   * Visual shape of the preview and crop mask. `'circle'` for profile avatars
   * and contacts; `'square'` for logos (including the 1:1 badge stamp).
   * Required — never inferred from the aspect ratio.
   *
   * A `'circle'` shape with `aspectRatio !== 1` is a type-level error: circles
   * are always 1:1.
   */
  shape: 'circle' | 'square';
  /**
   * The entity's FileRef for this asset (file-handling §14.3). Preferred over
   * `avatarUrl`: it carries the processing/failed states, so after a reload
   * during the ~16s processing window the editor shows an honest "processing"
   * frame instead of falling silently back to initials ("looks like it didn't
   * save", §7.1 rows 6-10). `avatarUrl` remains for callers whose image is not
   * a server FileRef (e.g. CreateContactDialog's pre-create blob preview).
   */
  fileRef?: FileRef | null;
  /** Current avatar URL */
  avatarUrl?: string;
  /** Fallback initials if no avatar */
  initials?: string;
  /** Custom placeholder element (e.g., SentimentIcon). Takes precedence over initials when no avatarUrl. */
  placeholder?: React.ReactNode;
  /**
   * Called when user saves new avatar. Reject on failure (never swallow): the
   * editor then returns to the OLD picture. It shows a file decline (422
   * `{code, message}`) inline itself; every other failure is the CALLER's to
   * report, exactly once (#885 D1). Guard:
   * `AvatarEditor.consumersNeverSwallow.guard.test.ts`.
   */
  onSave: (file: File, crop: CropData) => Promise<void>;
  /** Called when user confirms delete. Omit to hide delete button. */
  onDelete?: () => Promise<void>;
  /** Disable all interactions */
  disabled?: boolean;
  /** Border color (e.g., sentiment color for contacts) */
  borderColor?: string;
  /**
   * Aspect ratio for crop.
   *
   * - A **number** locks the crop to that exact ratio (1 for avatar, 3 for logo).
   * - A **`{ min, max }`** object lets the user crop freely within the bounds:
   *   `ReactCrop aspect={undefined}` (free drag), clamped so `min ≤ w/h ≤ max`
   *   on every change. Initial crop covers the whole image when the image ratio
   *   is within bounds, or the nearest bound centred otherwise.
   *
   * Default: `1`.
   */
  aspectRatio?: number | { min: number; max: number };
  /**
   * Background of the preview/upload area for the rectangular (non-circular)
   * variant. Defaults to the muted surface; pass the real in-context backdrop
   * (e.g. white for the 3:1 logo) so the preview shows exactly how the image
   * will appear in place.
   */
  previewBackground?: string;
  /**
   * Whether to run the crop step. Default true. Set false for assets the
   * backend stores AS-IS with no server-side crop (e.g. the transparent header
   * logo and the NOVI SSO button icon, served raw to preserve alpha): the
   * picked file is uploaded directly, skipping crop. `onSave` still receives a
   * CropData for signature compatibility, but it is zeroed and meaningless.
   */
  enableCrop?: boolean;
  /**
   * Accepted file types for the picker. Defaults to raster types that the
   * cropper can handle. No-crop callers pass transparent-friendly types
   * (e.g. '.png,.svg,image/png,image/svg+xml').
   */
  accept?: string;
  /**
   * Rectangular variant only: show the "Logo" word in the empty tile (default
   * true). The 30 px badge tile in the network settings shows nothing (#1548).
   */
  emptyLabel?: boolean;
}

type EditorState = 'display' | 'upload' | 'crop' | 'delete-confirm';

/** Whether the aspect ratio prop is a bounds object (`{ min, max }`). */
export function isBounds(ar: number | { min: number; max: number }): ar is { min: number; max: number } {
  return typeof ar === 'object';
}

/** Resolve a single numeric aspect from the prop — used where a fixed number is needed. */
function resolveAspect(ar: number | { min: number; max: number }): number {
  return isBounds(ar) ? ar.max : ar;
}

/** Clamp `ratio` into `[min, max]`. */
export function clampRatio(ratio: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, ratio));
}

/**
 * Helper to center a crop selection on an image at a fixed aspect ratio (90%
 * width, centred). Used for numeric `aspectRatio` values.
 */
function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
): Crop {
  return centerCrop(
    makeAspectCrop(
      { unit: '%', width: 90 },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

/**
 * Initial crop for bounds mode. If the image ratio is within bounds, the crop
 * covers 100% of the image. Otherwise the crop is the nearest bound at the
 * largest size that fits, centred.
 */
export function initialBoundsCrop(
  mediaWidth: number,
  mediaHeight: number,
  min: number,
  max: number,
): Crop {
  const imageRatio = mediaWidth / mediaHeight;
  if (imageRatio >= min && imageRatio <= max) {
    // Image is within bounds — full image.
    return { unit: '%', x: 0, y: 0, width: 100, height: 100 };
  }
  // Outside bounds: use the nearest bound.
  const targetAspect = imageRatio < min ? min : max;
  return centerCrop(
    makeAspectCrop(
      { unit: '%', width: 100 },
      targetAspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

/**
 * Clamp a percent crop so its w/h ratio stays within `[min, max]`. Adjusts
 * width or height (whichever keeps the crop within the image) without moving
 * the crop off the image.
 */
export function clampCropRatio(
  crop: Crop,
  mediaWidth: number,
  mediaHeight: number,
  min: number,
  max: number,
): Crop {
  if (crop.unit !== '%') return crop; // safety — we always work in %
  const w = crop.width;
  const h = crop.height;
  if (!w || !h) return crop;

  // Convert % to pixel-proportional ratio for comparison.
  const ratio = (w * mediaWidth) / (h * mediaHeight);
  if (ratio >= min && ratio <= max) return crop;

  const target = ratio < min ? min : max;
  // Try adjusting width first.
  let newW = (target * h * mediaHeight) / mediaWidth;
  let newH = h;
  if (newW > 100 || crop.x + newW > 100) {
    // Width would overflow — adjust height instead.
    newH = (w * mediaWidth) / (target * mediaHeight);
    newW = w;
    // Ensure height stays in bounds.
    if (newH > 100) newH = 100;
    if (crop.y + newH > 100) newH = 100 - crop.y;
    // Recompute width to match.
    newW = (target * newH * mediaHeight) / mediaWidth;
  }
  // Ensure x stays in bounds.
  let x = crop.x;
  if (x + newW > 100) x = 100 - newW;
  if (x < 0) x = 0;
  let y = crop.y;
  if (y + newH > 100) y = 100 - newH;
  if (y < 0) y = 0;

  return { unit: '%', x, y, width: newW, height: newH };
}

/**
 * Render a cropped region of a loaded image to a PNG data URL, for an optimistic
 * preview. Best-effort: returns null if the canvas isn't available (e.g. jsdom)
 * or the crop is degenerate, so callers can fall back to the raw upload.
 */
function cropToDataUrl(
  image: HTMLImageElement,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
): string | null {
  try {
    if (!sw || !sh) return null;
    const canvas = document.createElement('canvas');
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(image, sx, sy, sw, sh, 0, 0, sw, sh);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

/**
 * AvatarEditor - Self-contained avatar editing component
 *
 * Handles display, upload, crop, and delete flows all within the same
 * square container. Used in ProfileEditDialog and ContactDetail.
 */
/**
 * The extensions a given `accept` string permits, in the form
 * `validateUpload` wants. Handles both spellings callers use — dotted
 * extensions ('.png') and MIME types ('image/svg+xml').
 */
const MIME_EXTENSIONS: Record<string, string[]> = {
  'image/jpeg': ['jpg', 'jpeg'],
  'image/png': ['png'],
  'image/gif': ['gif'],
  'image/svg+xml': ['svg'],
  'image/webp': ['webp'],
};

export function acceptedExtensions(accept: string): string[] {
  const out = new Set<string>();
  for (const raw of accept.split(',')) {
    const entry = raw.trim().toLowerCase();
    if (!entry) continue;
    if (entry.startsWith('.')) out.add(entry.slice(1));
    else for (const ext of MIME_EXTENSIONS[entry] ?? []) out.add(ext);
  }
  return Array.from(out);
}

export function AvatarEditor({
  shape,
  fileRef,
  avatarUrl,
  initials,
  placeholder,
  onSave,
  onDelete,
  disabled = false,
  borderColor,
  aspectRatio = 1,
  previewBackground = vars.color.surface.muted,
  enableCrop = true,
  accept = '.jpg,.jpeg,.png,.gif',
  emptyLabel = true,
}: AvatarEditorProps) {
  if (import.meta.env.DEV && shape === 'circle' && aspectRatio !== 1) {
    throw new Error(
      `AvatarEditor: shape="circle" requires aspectRatio=1 (got ${JSON.stringify(aspectRatio)}). ` +
      'A circle is always 1:1.',
    );
  }

  const hasBounds = isBounds(aspectRatio);
  const numericAspect = resolveAspect(aspectRatio);
  const isCircular = shape === 'circle';
  const [state, setState] = useState<EditorState>('display');
  const [hovered, setHovered] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop | undefined>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | undefined>();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // The server processes the uploaded image asynchronously (crop + resize), so
  // the persisted URL isn't ready when onSave resolves. Show a client-cropped
  // preview of what the user just saved until the parent refreshes with the
  // processed asset. Persists for this editor instance (dialogs unmount on close).
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  // The FileRef the editor showed when the save that set `localPreview`
  // started (#885). A `failed` file beats the preview (rule: failed is shown,
  // I3) unless it is still THAT file: a stale re-read after the save can hand
  // the old failed FileRef back, and that is not the new picture failing.
  const [previewOver, setPreviewOver] = useState<FileRef | null | undefined>(null);
  // Visible client-side rejection (§14.4 Q6 / rule (b)) — this used to be a
  // silent `return` on a disallowed MIME type.
  const [uploadError, setUploadError] = useState<string | null>(null);

  const validate = useUploadValidation();
  const fileView = useFileRef(fileRef);
  // Failed beats preview (#885 R4, I3): a file the server reports `failed` is
  // shown as failed even when this session holds a local preview — unless the
  // failed FileRef is the one the save replaced (a stale re-read). Decided by
  // `sameFileRef`, the one FileRef equality the client has (#885a-4 R5).
  const showFailed =
    fileView.state === 'failed' && (!localPreview || !sameFileRef(fileRef, previewOver));
  // Display precedence (§14.3.6, adapted): the client-cropped preview wins for
  // this session — it is what the user will end up with, and more accurate
  // than the server's uncropped echo — while the file is processing or ready.
  // Never over a failed file. Otherwise the ready rendition, then the caller's
  // plain URL.
  const displayUrl = (showFailed ? null : localPreview) ?? fileView.url ?? avatarUrl;
  // Reload during processing: no local preview and no rendition. Say so rather
  // than falling back to initials, which reads as "the avatar never saved".
  const showProcessing = !localPreview && !displayUrl && fileView.state === 'processing';

  const imgRef = useRef<HTMLImageElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Display-state preview ratio for bounds mode (R4): the box ratio tracks
  // the loaded image's natural ratio clamped to the bounds. Before the image
  // loads, the box uses the max bound so it is wide enough.
  const [displayRatio, setDisplayRatio] = useState<number | null>(null);

  // No-crop save: upload the picked file directly (the backend stores it as-is).
  // Crop is meaningless here, so we hand onSave a zeroed CropData purely to keep
  // the signature. Shows an optimistic local preview until the parent refreshes.
  const saveWithoutCrop = useCallback(async (file: File) => {
    const savedOver = fileRef;
    setSaving(true);
    try {
      await onSave(file, { startX: 0, startY: 0, width: 0, height: 0 });
      const reader = new FileReader();
      reader.onload = () => {
        setLocalPreview(reader.result as string);
        setPreviewOver(savedOver);
      };
      reader.readAsDataURL(file);
      setState('display');
    } catch (error) {
      // A failed save leaves the OLD picture on display (#885 D3). The
      // server's synchronous decline (422 {code, message}) is said right here,
      // under the editor, while the person is still looking (plan A1.1).
      // Every other failure is the caller's to report, once (#885 D1): saying
      // it here too would say it twice.
      setUploadError(extractFileDecline(error)?.message ?? null);
      setState('display');
    } finally {
      setSaving(false);
    }
  }, [onSave, fileRef]);

  // Handle file selection
  const handleFileSelect = useCallback((file: File) => {
    // Extension + per-type size cap, mirroring the server (file-handling
    // §14.4 Q6). Rule (b): a rejection has to be VISIBLE while the person is
    // still sitting there — the old code silently dropped a bad MIME type,
    // which was indistinguishable from a broken upload button.
    const verdict = validate(file, { allowedExtensions: acceptedExtensions(accept) });
    if (!verdict.ok) {
      setUploadError(verdict.message);
      return;
    }
    setUploadError(null);

    if (!enableCrop) {
      void saveWithoutCrop(file);
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setState('crop');
    };
    reader.readAsDataURL(file);
  }, [enableCrop, saveWithoutCrop, validate, accept]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    if (inputRef.current) inputRef.current.value = '';
  }, [handleFileSelect]);

  // Drag and drop
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  // Image load handler for cropper
  // Image load handler for cropper.
  // Sets both the percent crop (for the overlay) and the completed pixel crop
  // (so Save is immediately available, especially in bounds mode where the
  // initial crop can cover 100% of the image).
  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    let percentCrop: Crop;
    if (hasBounds) {
      percentCrop = initialBoundsCrop(width, height, aspectRatio.min, aspectRatio.max);
    } else {
      percentCrop = centerAspectCrop(width, height, numericAspect);
    }
    setCrop(percentCrop);
    // Derive the pixel crop from the percent crop so completedCrop is set
    // immediately (ReactCrop fires onComplete only on user interaction).
    if ('width' in percentCrop && 'height' in percentCrop && percentCrop.unit === '%') {
      setCompletedCrop({
        unit: 'px',
        x: Math.round((percentCrop.x ?? 0) * width / 100),
        y: Math.round((percentCrop.y ?? 0) * height / 100),
        width: Math.round((percentCrop.width ?? 0) * width / 100),
        height: Math.round((percentCrop.height ?? 0) * height / 100),
      });
    }
  }, [aspectRatio, hasBounds, numericAspect]);

  // Save handler
  const handleSave = useCallback(async () => {
    if (!selectedFile || !completedCrop || !imgRef.current) return;

    const image = imgRef.current;
    const { naturalWidth, naturalHeight } = image;
    const scaleX = naturalWidth / image.width;
    const scaleY = naturalHeight / image.height;

    const cropData: CropData = {
      startX: Math.round(completedCrop.x * scaleX),
      startY: Math.round(completedCrop.y * scaleY),
      width: Math.round(completedCrop.width * scaleX),
      height: Math.round(completedCrop.height * scaleY),
    };

    // Capture an optimistic preview of the cropped result while the cropper
    // image is still mounted (falls back to the full upload if canvas is
    // unavailable). Shown until the parent refreshes with the processed asset.
    const previewUrl =
      cropToDataUrl(image, cropData.startX, cropData.startY, cropData.width, cropData.height) ??
      imageSrc;

    const savedOver = fileRef;
    setSaving(true);
    try {
      await onSave(selectedFile, cropData);
      if (previewUrl) {
        setLocalPreview(previewUrl);
        setPreviewOver(savedOver);
      }
    } catch (error) {
      // A failed save returns to DISPLAY with the OLD picture (#885 D3); the
      // frame click is Replace. A decline (422 {code, message}) is said here,
      // inline under the editor, while the person is still there (plan
      // A1.1). Every other failure is the caller's to report, once (#885 D1).
      setUploadError(extractFileDecline(error)?.message ?? null);
    } finally {
      setSaving(false);
      setState('display');
      setSelectedFile(null);
      setImageSrc(null);
      setCrop(undefined);
      setCompletedCrop(undefined);
    }
  }, [selectedFile, completedCrop, onSave, imageSrc, fileRef]);

  // Delete handler
  const handleDelete = useCallback(async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete();
      setLocalPreview(null);
      setPreviewOver(null);
      setState('display');
    } catch {
      // The caller owns the failure message (it has already said why) — #845.
      // Swallow the rejection: nothing awaits this click handler, so a re-throw
      // is an unhandled rejection. The decision settled, so the confirm closes
      // (a confirm has nothing to fix an error with); the current avatar stays.
      setState('display');
    } finally {
      setDeleting(false);
    }
  }, [onDelete]);

  // Reset to display
  const handleCancel = useCallback(() => {
    setState('display');
    setSelectedFile(null);
    setImageSrc(null);
    setCrop(undefined);
    setCompletedCrop(undefined);
  }, []);

  // Go back from crop to upload
  const handleBackToCrop = useCallback(() => {
    setState('upload');
    setSelectedFile(null);
    setImageSrc(null);
    setCrop(undefined);
    setCompletedCrop(undefined);
  }, []);

  const openFilePicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  // Entering edit from the display state: crop mode opens the upload/crop flow;
  // no-crop mode goes straight to the file picker (pick → upload, no steps).
  const beginEdit = () => {
    if (disabled) return;
    if (enableCrop) setState('upload');
    else openFilePicker();
  };

  // Container sizing changes for crop state
  const isCropState = state === 'crop';
  const borderRadius = isCircular ? '50%' : 2;

  // Effective aspect ratio for the outer container in display/upload states.
  const containerAspect = hasBounds
    ? (displayRatio ?? aspectRatio.max)
    : numericAspect;

  // Honest states for a file the server has but can't render yet (or couldn't
  // process at all). Both stay inside the avatar/logo frame and remain
  // clickable, so the upload affordance IS the retry path (§13 V2/V3).
  const statusFrame =
    showProcessing || showFailed ? (
      <div
        onClick={beginEdit}
        data-testid={showFailed ? testIds.files.failed : testIds.files.processing}
        style={{
          width: '100%',
          height: '100%',
          borderRadius,
          backgroundColor: isCircular ? vars.color.surface.border : previewBackground,
          border: borderColor ? `3px solid ${borderColor}` : `1px solid ${vars.color.surface.border}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          padding: 8,
          textAlign: 'center',
          overflow: 'hidden',
          boxSizing: 'border-box',
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        {showFailed ? (
          <>
            <Text variant="caption" color="error">
              {formMessages.upload.failed}
            </Text>
            <Text variant="caption" color="secondary">
              {formMessages.upload.retry}
            </Text>
          </>
        ) : (
          <>
            <Spinner size={24} />
            <Text variant="caption" color="secondary">
              {formMessages.upload.processing(fileRefProcessingCopy(fileView.type))}
            </Text>
          </>
        )}
      </div>
    ) : null;

  return (
    // AspectBox, not CSS aspect-ratio (Chrome 88; the floor is Chrome 80, #981
    // 981d). The crop state sizes by its content; the tree stays the same.
    <AspectBox
      ratio={isCropState ? null : containerAspect}
      style={
        isCropState
          ? {
              maxWidth: 300,
              maxHeight: 200,
            }
          : {
              width: '100%',
              maxWidth: containerAspect === 1 ? 200 : 300,
            }
      }
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {/* DISPLAY STATE */}
      {state === 'display' && (
        <>
          {statusFrame ? (
            statusFrame
          ) : isCircular ? (
            // For circular avatars: show avatar if URL exists, or custom placeholder, or initials fallback
            displayUrl ? (
              <Avatar
                src={displayUrl}
                style={{
                  // Block-level flex (not the primitive's inline-flex): an inline
                  // avatar sits on the text baseline with a descender gap below it,
                  // so the absolute hover scrim (which fills the box) peeks ~2px
                  // out at the bottom. Block-level removes that gap.
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  // Until the photo paints this must BE the no-photo state: the
                  // same fallback below, on the same backdrop, with no ring —
                  // the ring frames a photo, so it waits for one.
                  backgroundColor: placeholder ? 'transparent' : vars.color.surface.border,
                  color: vars.color.neutral.base,
                  fontSize: 48,
                  cursor: disabled ? 'default' : 'pointer',
                  transition: `opacity ${transitions.duration.standard} ${transitions.easing.standard}`,
                  opacity: hovered && !disabled ? 0.7 : 1,
                }}
                loadedStyle={{
                  backgroundColor: vars.color.surface.border,
                  border: borderColor ? `3px solid ${borderColor}` : undefined,
                }}
                onClick={beginEdit}
              >
                {placeholder ?? initials}
              </Avatar>
            ) : placeholder ? (
              <div
                onClick={beginEdit}
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: disabled ? 'default' : 'pointer',
                  transition: `opacity ${transitions.duration.standard} ${transitions.easing.standard}`,
                  opacity: hovered && !disabled ? 0.7 : 1,
                }}
              >
                {placeholder}
              </div>
            ) : initials ? (
              <Avatar
                style={{
                  display: 'flex', // block-level: avoid the inline-baseline gap (see above)
                  width: '100%',
                  height: '100%',
                  backgroundColor: vars.color.surface.border,
                  color: vars.color.neutral.base,
                  fontSize: 48,
                  cursor: disabled ? 'default' : 'pointer',
                  transition: `opacity ${transitions.duration.standard} ${transitions.easing.standard}`,
                  opacity: hovered && !disabled ? 0.7 : 1,
                  border: borderColor ? `3px solid ${borderColor}` : undefined,
                }}
                onClick={beginEdit}
              >
                {initials}
              </Avatar>
            ) : (
              // No avatar AND no initials (e.g. a new contact before a name is
              // typed): a blank green disc looks broken, so show a neutral
              // "add a photo" affordance. Once a name yields initials the green
              // avatar above renders instead.
              <div
                onClick={beginEdit}
                data-testid={testIds.form.avatarEditorEmptyPlaceholder}
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  backgroundColor: vars.color.surface.muted,
                  border: borderColor ? `3px solid ${borderColor}` : `1px solid ${vars.color.surface.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: disabled ? 'default' : 'pointer',
                  transition: `opacity ${transitions.duration.standard} ${transitions.easing.standard}`,
                  opacity: hovered && !disabled ? 0.7 : 1,
                }}
              >
                <Icons.addPhoto style={{ fontSize: 48, color: vars.color.neutral.subtle }} />
              </div>
            )
          ) : (
            <div
              onClick={beginEdit}
              style={{
                width: '100%',
                height: '100%',
                borderRadius,
                backgroundColor: previewBackground,
                cursor: disabled ? 'default' : 'pointer',
                transition: `opacity ${transitions.duration.standard} ${transitions.easing.standard}`,
                opacity: hovered && !disabled ? 0.7 : 1,
                border: borderColor ? `3px solid ${borderColor}` : `1px solid ${vars.color.surface.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {displayUrl ? (
                <img
                  src={displayUrl}
                  alt={formMessages.avatar.logo}
                  onLoad={hasBounds ? (e) => {
                    const img = e.currentTarget;
                    const nat = img.naturalWidth / img.naturalHeight;
                    setDisplayRatio(clampRatio(nat, aspectRatio.min, aspectRatio.max));
                  } : undefined}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                (initials || emptyLabel) && (
                  <Text variant="cardTitle" color="secondary">
                    {initials || formMessages.avatar.logo}
                  </Text>
                )
              )}
            </div>
          )}

          {/* Hover overlay with edit icon */}
          {hovered && !disabled && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: overlays.avatar,
                borderRadius,
                cursor: 'pointer',
              }}
              onClick={beginEdit}
            >
              <Icons.edit style={{ color: 'white', fontSize: 32 }} />
            </div>
          )}

          {/* Delete button - bottom left */}
          {hovered && !disabled && onDelete && displayUrl && (
            <div style={{ position: 'absolute', bottom: 0, left: 0 }}>
              <ActionButton
                action={Actions.delete}
                mode="icon-overlay"
                onClick={(e) => {
                  e.stopPropagation();
                  setState('delete-confirm');
                }}
                testIdOverride={testIds.form.avatarEditorDeleteButton}
              />
            </div>
          )}

          {/* No-crop uploads save while still in the display state — show a
              busy overlay so the pick→upload has visible feedback. */}
          {saving && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: overlays.avatar,
                borderRadius,
              }}
            >
              <Spinner size={24} />
            </div>
          )}
        </>
      )}

      {/* DELETE CONFIRM STATE */}
      {state === 'delete-confirm' && (
        <>
          {isCircular ? (
            <Avatar
              src={displayUrl}
              style={{
                display: 'flex', // block-level: avoid the inline-baseline gap (see above)
                width: '100%',
                height: '100%',
                backgroundColor: vars.color.surface.border,
                color: vars.color.neutral.base,
                fontSize: 48,
                opacity: 0.5,
                border: borderColor ? `3px solid ${borderColor}` : undefined,
              }}
            >
              {initials}
            </Avatar>
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius,
                backgroundColor: previewBackground,
                opacity: 0.5,
                border: borderColor ? `3px solid ${borderColor}` : `1px solid ${vars.color.surface.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {displayUrl && (
                <img
                  src={displayUrl}
                  alt="Logo"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              )}
            </div>
          )}

          {/* Confirmation overlay */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: overlays.confirm,
              borderRadius,
              gap: vars.spacing.sm,
            }}
          >
            <Text style={{ color: 'white', fontSize: 14, fontWeight: 500 }}>
              {formMessages.avatar.removeConfirm}
            </Text>
            <div style={{ display: 'flex', gap: vars.spacing.sm }}>
              <ActionButton
                action={Actions.delete}
                mode="icon-confirm"
                size="small"
                onClick={handleDelete}
                disabled={deleting}
                loading={deleting}
                testIdOverride={testIds.form.avatarEditorConfirmDeleteButton}
              />
              <ActionButton
                action={Actions.cancel}
                mode="icon-confirm"
                size="small"
                onClick={() => setState('display')}
                disabled={deleting}
                testIdOverride={testIds.form.avatarEditorCancelButton}
              />
            </div>
          </div>
        </>
      )}

      {/* UPLOAD STATE */}
      {state === 'upload' && (
        <>
          {/* Dropzone */}
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={openFilePicker}
            className={dropzoneClass}
            style={{
              width: '100%',
              height: '100%',
              borderRadius,
              border: '2px dashed',
              borderColor: isDragging ? vars.color.success.base : vars.color.neutral.subtle,
              backgroundColor: isDragging ? vars.color.success.tint : previewBackground,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: transitions.allEased,
            }}
          >
            <Icons.cloudUpload style={{ fontSize: 32, color: vars.color.neutral.subtle, marginBottom: vars.spacing.xs }} />
            <Text variant="caption" color="secondary" style={{ textAlign: 'center', paddingLeft: '16px', paddingRight: '16px' }}>
              {formMessages.avatar.dropOrClick}
            </Text>
          </div>

          {/* Back button - top left */}
          <div style={{ position: 'absolute', top: 0, left: 0 }}>
            <ActionButton
              action={Actions.back}
              mode="icon-overlay"
              onClick={handleCancel}
              testIdOverride={testIds.form.avatarEditorBackButton}
            />
          </div>
        </>
      )}

      {/* CROP STATE */}
      {state === 'crop' && imageSrc && (
        <>
          {/* Image with crop overlay */}
          <div
            style={{
              position: 'relative',
              display: 'inline-block',
            }}
          >
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => {
                if (hasBounds && imgRef.current) {
                  const { width: mw, height: mh } = imgRef.current;
                  setCrop(clampCropRatio(percentCrop, mw, mh, aspectRatio.min, aspectRatio.max));
                } else {
                  setCrop(percentCrop);
                }
              }}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={hasBounds ? undefined : numericAspect}
              circularCrop={isCircular}
              disabled={saving}
              style={{ display: 'block' }}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop preview"
                onLoad={handleImageLoad}
                style={{
                  display: 'block',
                  maxWidth: 300,
                  maxHeight: 200,
                }}
              />
            </ReactCrop>

            {/* Back button - top left */}
            <div style={{ position: 'absolute', top: 4, left: 4 }}>
              <ActionButton
                action={Actions.back}
                mode="icon-overlay"
                onClick={handleBackToCrop}
                disabled={saving}
                testIdOverride={testIds.form.avatarEditorBackButton}
              />
            </div>

            {/* Save button - bottom right */}
            <div style={{ position: 'absolute', bottom: 4, right: 4 }}>
              <ActionButton
                action={Actions.save}
                mode="icon-confirm"
                onClick={handleSave}
                disabled={!completedCrop || saving}
                loading={saving}
                testIdOverride={testIds.form.avatarEditorSaveButton}
              />
            </div>
          </div>
        </>
      )}

      {/* Client-side rejection — rendered outside the fixed-ratio frame so it
          never resizes the avatar, but always on screen next to it. */}
      {uploadError && (
        <div
          data-testid={testIds.form.avatarEditorUploadError}
          style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4 }}
        >
          <Text variant="caption" color="error" style={{ display: 'block', textAlign: 'center' }}>
            {uploadError}
          </Text>
        </div>
      )}
    </AspectBox>
  );
}
